import { useMemo } from 'react';
import { useFetchAllAnalysedApplicationsByCandidateBatch } from './useFetchAllAnalysedApplicationsByCandidateBatch';
import { useFetchAllSourcedCandidatesByCandidateBatch } from './useFetchAllSourcedCandidatesByCandidateBatch';

export type DuplicateCandidateProfileResult = {
  candidateProfileId: string;
  hasSourcedInvite: boolean;
  hasAnalysedInvite: boolean;
  hasDuplicate: boolean;
};

type UseSourcingInternalCandidateProfileDuplicateCheckProps = {
  candidateProfileIds: string[];
  candidateBatchId: string | null;
};

export const useSourcingInternalCandidateProfileDuplicateCheck = ({
  candidateProfileIds,
  candidateBatchId
}: UseSourcingInternalCandidateProfileDuplicateCheckProps) => {
  const { sourcedCandidates, loading: isSourcedCandidatesLoading } =
    useFetchAllSourcedCandidatesByCandidateBatch({ candidateBatchId });
  const { analysedApplications, loading: isAnalysedApplicationsLoading } =
    useFetchAllAnalysedApplicationsByCandidateBatch({ candidateBatchId });

  const duplicateCandidateProfileResults: DuplicateCandidateProfileResult[] =
    useMemo(() => {
      const sourcedProfileIds = new Set(
        sourcedCandidates?.candidateProfilesIds ?? []
      );
      const analysedProfileIds = new Set(
        analysedApplications?.candidateProfilesIds ?? []
      );

      return candidateProfileIds.map((candidateProfileId) => {
        const hasSourcedInvite = sourcedProfileIds.has(candidateProfileId);
        const hasAnalysedInvite = analysedProfileIds.has(candidateProfileId);
        const hasDuplicate = hasSourcedInvite || hasAnalysedInvite;
        return {
          candidateProfileId,
          hasSourcedInvite,
          hasAnalysedInvite,
          hasDuplicate
        };
      });
    }, [candidateProfileIds, sourcedCandidates, analysedApplications]);

  return {
    isLoading: isSourcedCandidatesLoading || isAnalysedApplicationsLoading,
    duplicateCandidateProfileResults
  };
};
